import { Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { map, size } from 'es-toolkit/compat';
import React from 'react';

import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { FeaturesExportForm } from 'components/ResultsExport/FeaturesExportForm';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { useResultsExportContext } from 'components/ResultsExport/useResultsExportContext';
import { toWorkflowResultsExportContext } from 'components/ResultsExport/workflowResultsExportContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { FeaturesExportGroup } from 'services/export/exportFeaturesForGroups';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { getFeatureRequestGroups, ResultPick } from 'services/export/workflowResultsSelection';
import { useWorkflowResultsSelection } from './useWorkflowResultsSelection';
import { WorkflowResultsSelection } from './WorkflowResultsSelection';

interface WorkflowFeaturesExportDialogProps {
  study?: Study;
  onClose: () => void;
}

const toResultsMode = (pick: ResultPick) =>
  pick.source === 'published'
    ? ResultsMode.Published
    : pick.source === 'approved'
    ? ResultsMode.InternallyApproved
    : ResultsMode.Manual;

const countLabel = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// Opened from Review and publication. Features for the whole study, download only, never the Dataset chooser.
// The slides and the results of each come from the panel → slides selection; one CSV is requested for each panel
// and results source, and the CSVs are never merged.
export const WorkflowFeaturesExportDialog: React.FC<WorkflowFeaturesExportDialogProps> = ({ study, onClose }) => {
  const baseContext = useResultsExportContext(study);
  const controller = useWorkflowResultsSelection('features', study);
  const { source, selection, panelsInput, getRunLabel } = controller;
  const { startExport, exportProgressDialog, isExportRunning } = useStudyExportController();
  const { clearDraft } = useResultsExportUrlState();

  const close = () => {
    clearDraft('features');
    controller.close();
    onClose();
  };

  const requestGroups = React.useMemo(() => getFeatureRequestGroups(selection), [selection]);
  const describeSource = React.useCallback(
    (pick: ResultPick) =>
      pick.source === 'published'
        ? 'Published results'
        : pick.source === 'approved'
        ? 'Approved results'
        : getRunLabel(pick.runId as string),
    [getRunLabel]
  );

  const exportGroups = React.useMemo(
    (): FeaturesExportGroup[] =>
      map(requestGroups, ({ key, panelName, pick, slideIds }) => {
        const resultsMode = toResultsMode(pick);
        const { casesParamsWithoutPaginationAndSorting } = toWorkflowResultsExportContext(baseContext, {
          resultsMode,
          orchestrationId: pick.runId,
        });
        return {
          key,
          title: `${panelName}, ${describeSource(pick)}`,
          fileStem: `${panelName}_${pick.source === 'run' ? `run_${pick.runId}` : pick.source}`,
          resultsMode,
          orchestrationId: pick.runId,
          casesParams: { ...casesParamsWithoutPaginationAndSorting, slideIdsToInclude: slideIds },
        };
      }),
    [requestGroups, baseContext, describeSource]
  );
  const [firstGroup] = exportGroups;
  const context = React.useMemo(
    () =>
      toWorkflowResultsExportContext(baseContext, {
        resultsMode: firstGroup?.resultsMode ?? ResultsMode.Published,
        orchestrationId: firstGroup?.orchestrationId,
      }),
    [baseContext, firstGroup?.resultsMode, firstGroup?.orchestrationId]
  );

  const { availability, hasNoResults } = source;
  // Without the unpublished-results permission there is no source to choose, a study that does not allow
  // export has nothing to choose either, and neither has one without any results, so an unavailable export ends here.
  const isUnavailableForUser =
    availability.disabled && (!source.canViewUnpublishedResults || !study?.allowExport || hasNoResults);
  const blockedReason = availability.disabled
    ? availability.note
    : panelsInput.isLoading
    ? 'Loading panel information across the cohort…'
    : panelsInput.error
    ? 'The panels could not be loaded, so the slides cannot be grouped by panel.'
    : exportGroups.length === 0
    ? source.selection.source === 'run' &&
      !panelsInput.isLoading &&
      !panelsInput.error &&
      selection.selectableSlideCount === 0
      ? 'Select at least one post-processing run.'
      : 'Select at least one slide with a result to export.'
    : undefined;

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="xl"
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby="workflow-features-export-title"
      >
        <DialogChromeHeader
          titleId="workflow-features-export-title"
          title="Export features"
          onClose={close}
          closeButtonAriaLabel="Close export"
        />
        {isUnavailableForUser || !study ? (
          <>
            <DialogContent dividers>
              <Typography variant="body2">{availability.note}</Typography>
            </DialogContent>
            <DialogChromeFooter>
              <Button color="inherit" onClick={close}>
                Close
              </Button>
            </DialogChromeFooter>
          </>
        ) : (
          <FeaturesExportForm
            context={context}
            startExport={startExport}
            isExportRunning={isExportRunning}
            onBack={close}
            backLabel="Close"
            downloadOnly
            resultSourceOwner="Workflow"
            blockedReason={blockedReason}
            exportGroups={exportGroups}
            resultsSelection={
              <WorkflowResultsSelection
                study={study}
                kind="features"
                controller={controller}
                operation="Export features"
                description="Calculated features from the Cell typing & marker positivity runs."
              >
                {size(requestGroups) > 0 && (
                  <Stack spacing={0.5} role="group" aria-label="Features CSVs to download">
                    <Typography variant="subtitle2">
                      {countLabel(size(requestGroups), 'features CSV')}
                      {size(requestGroups) > 1 ? ' in one ZIP' : ''}
                    </Typography>
                    {map(requestGroups, ({ key, panelName, pick, slideIds }) => (
                      <Typography key={key} variant="body2">
                        <strong>{panelName}</strong> / {describeSource(pick)}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {countLabel(size(slideIds), 'selected slide')}
                        </Typography>
                      </Typography>
                    ))}
                  </Stack>
                )}
              </WorkflowResultsSelection>
            }
          />
        )}
      </Dialog>
      {exportProgressDialog}
    </>
  );
};
